import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {scaleNumber} from "../../helpers/scaling";
import {formatTimeRange} from "../../helpers/tooltips";
import {debugLog} from '../../helpers/debug_log';

export class ShaperCircuitDevices extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.containerId = `circuitDevices_${slot}`;
        this.loadingId = `circuitDevicesLoading_${slot}`;
        this.graphs = [];
        this.explainIssues = null;
        this.lastDevices = null;
        this.circuitCapacity = null;
    }

    title() {
        return "Devices";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Circuit Devices", issues: this.explainIssues };
    }

    channelRequests() {
        return ["CircuitDevices", "CircuitHeader"];
    }

    buildContainer() {
        const base = super.buildContainer();
        const loading = document.createElement("div");
        loading.id = this.loadingId;
        loading.className = "text-muted small py-3";
        loading.innerHTML = "<i class='fa fa-spinner fa-spin me-2'></i>Loading devices...";
        base.appendChild(loading);

        const wrapper = document.createElement("div");
        wrapper.id = this.containerId;
        wrapper.classList.add("row", "g-3");
        base.appendChild(wrapper);
        return base;
    }

    disposeCharts() {
        if (this.graphs && this.graphs.length > 0) {
            this.graphs.forEach((g) => {
                if (g && typeof g.dispose === "function") {
                    g.dispose();
                } else if (g?.chart) {
                    g.chart.dispose();
                }
            });
        }
        this.graphs = [];
    }

    dispose() {
        this.disposeCharts();
        super.dispose();
    }

    onTabActivated() {
        super.onTabActivated();
        if (this.lastDevices === null) {
            this.showLoading();
        }
        if (this.graphs && this.graphs.length > 0) {
            this.graphs.forEach((g) => {
                if (g.chart && g.chart.resize) {
                    g.chart.resize();
                }
            });
        }
    }

    onMessage(msg) {
        if (!msg.Data) return;
        if (msg.Data.target === "circuit_header") {
            this.circuitCapacity = msg.Data.blob || null;
            if (this.lastDevices) {
                this.explainIssues = buildDeviceIssues(this.lastDevices, this.circuitCapacity);
            }
            return;
        }
        if (msg.Data.target !== "circuit_devices") return;
        const payload = msg.Data.blob || {};
        const devices = payload.devices || [];
        this.lastDevices = devices;
        this.hideLoading();
        this.renderDevices(devices);
        this.explainIssues = buildDeviceIssues(devices, this.circuitCapacity);
    }

    onLoadStart(requestedChannels = []) {
        if (requestedChannels.includes("CircuitDevices")) {
            this.showLoading();
        }
    }

    showLoading() {
        const loading = document.getElementById(this.loadingId);
        if (loading) {
            loading.style.display = "";
        }
    }

    hideLoading() {
        const loading = document.getElementById(this.loadingId);
        if (loading) {
            loading.style.display = "none";
        }
    }

    renderDevices(devices) {
        debugLog("Rendering devices", devices);
        const container = document.getElementById(this.containerId);
        if (!container) return;
        this.disposeCharts();
        container.innerHTML = "";

        if (!devices || devices.length === 0) {
            const empty = document.createElement("div");
            empty.className = "col-12";
            empty.innerHTML = `<div class="alert alert-warning mb-0">No devices were found for this circuit in the selected time range.</div>`;
            container.appendChild(empty);
            return;
        }

        devices.forEach((device, idx) => {
            const col = document.createElement("div");
            col.className = "col-12 col-xl-6";

            const card = document.createElement("div");
            card.className = "card shadow-sm h-100";

            const body = document.createElement("div");
            body.className = "card-body";

            const titleRow = document.createElement("div");
            titleRow.className = "d-flex justify-content-between align-items-start mb-2";

            const title = document.createElement("div");
            title.className = "redactable";
            title.innerHTML = `<h5 class="card-title mb-0">${device.device_name || device.device_id || "Device"}</h5>`;
            titleRow.appendChild(title);

            const badges = document.createElement("div");
            if (device.has_general) {
                badges.appendChild(this.buildBadge("General", "primary"));
            }
            if (device.has_wireless) {
                badges.appendChild(this.buildBadge("Wireless", "success"));
            }
            if (device.removed) {
                badges.appendChild(this.buildBadge("Removed", "danger"));
            }
            if (badges.childElementCount > 0) {
                badges.classList.add("d-flex", "gap-1", "flex-wrap");
                titleRow.appendChild(badges);
            }

            body.appendChild(titleRow);

            const subtitle = document.createElement("p");
            subtitle.className = "text-muted small mb-2 redactable";
            subtitle.innerText = device.device_id ? `ID: ${device.device_id}` : "";
            body.appendChild(subtitle);

            const details = document.createElement("dl");
            details.className = "row mb-2 small";
            const addDetail = (label, value, alwaysShow = false) => {
                if (!alwaysShow && (value === null || value === undefined || value === "")) {
                    return;
                }
                const dt = document.createElement("dt");
                dt.className = "col-5 text-muted mb-1";
                dt.innerText = label;
                const dd = document.createElement("dd");
                dd.className = "col-7 mb-1 redactable";
                dd.innerText = value === null || value === undefined || value === "" ? "-" : value;
                details.appendChild(dt);
                details.appendChild(dd);
            };
            addDetail("MAC Address", device.mac_address || "-", true);
            addDetail("Comment", device.comment || "-", true);
            addDetail("Model Code", device.model_code);
            addDetail("Model Name", device.model_name);
            addDetail("Vendor", device.vendor);
            addDetail("Mode", device.mode);
            addDetail("Firmware", device.firmware_version);

            if (device.last_seen) {
                addDetail("Last Seen", this.formatDate(device.last_seen));
            }
            if (device.removed) {
                addDetail("Removed", this.formatDate(device.removed));
            }

            body.appendChild(details);

            // Attach to DOM before rendering charts to ensure containers exist
            card.appendChild(body);
            col.appendChild(card);
            container.appendChild(col);

            const general = device.general_timeseries;
            const wireless = device.wireless_timeseries;

            if (general) {
                this.renderGeneralSection(body, device, general);
            }

            if (wireless) {
                this.renderWirelessSection(body, device, wireless);
            }
        });
    }

    renderGeneralSection(parent, device, general) {
        const hasCpu = this.hasValues(general.cpu_percent);
        const hasMem = this.hasValues(general.memory_percent);
        const hasTemp = this.hasValues(general.temperature_celsius);
        const hasGeneralData = hasCpu || hasMem || hasTemp;

        if (!hasGeneralData && !this.hasValues(general.uptime_seconds)) {
            return;
        }

        const section = document.createElement("div");
        section.className = "mt-3";
        parent.appendChild(section);
        const heading = document.createElement("div");
        heading.className = "d-flex align-items-center justify-content-between mb-1";
        heading.innerHTML = `<h6 class="text-secondary fw-semibold mb-0">General</h6>`;
        section.appendChild(heading);

        const uptimeWarning = this.uptimeWentBackwards(general.uptime_seconds);
        const latestUptime = this.latestValue(general.uptime_seconds);
        const latestOutage = this.latestValue(general.outage_score);
        if (uptimeWarning || latestUptime !== null || latestOutage !== null) {
            const meta = document.createElement("div");
            meta.className = "small mb-2";
            if (latestUptime !== null) {
                meta.innerHTML += `<span class="me-3"><i class="fa fa-clock me-1"></i>Uptime: ${this.formatNumber(latestUptime, 0)}s</span>`;
            }
            if (latestOutage !== null) {
                meta.innerHTML += `<span class="me-3"><i class="fa fa-bolt me-1"></i>Outage Score: ${this.formatNumber(latestOutage, 2)}</span>`;
            }
            if (uptimeWarning) {
                meta.innerHTML += `<span class="text-warning"><i class="fa fa-exclamation-triangle me-1"></i>Uptime decreased</span>`;
            }
            section.appendChild(meta);
        }

            if (hasGeneralData) {
                const graphId = `${this.id}_general_${device.device_hash}`;
                section.appendChild(this.makeGraphDiv(graphId));
                const series = [];
                if (hasCpu) {
                series.push({
                    name: "CPU %",
                    values: general.cpu_percent,
                    formatter: (v) => `${this.formatNumber(v, 1)}%`
                });
            }
            if (hasMem) {
                series.push({
                    name: "Memory %",
                    values: general.memory_percent,
                    formatter: (v) => `${this.formatNumber(v, 1)}%`
                });
            }
            if (hasTemp) {
                series.push({
                    name: "Temperature (C)",
                    values: general.temperature_celsius,
                    formatter: (v) => `${this.formatNumber(v, 1)}°C`
                });
            }
            this.buildChart(
                graphId,
                general.ticks || [],
                series,
                (v) => this.formatNumber(v, 1),
                (name, v, formatter) => (formatter ? formatter(v) : this.formatNumber(v, 1)),
                { title: "CPU/RAM", legendBottom: true }
            );
        }

    }

    renderWirelessSection(parent, device, wireless) {
        const hasUplink = this.hasValues(wireless.uplink_capacity);
        const hasDownlink = this.hasValues(wireless.downlink_capacity);
        const hasCapacity = hasUplink || hasDownlink;
        const hasLatency = this.hasValues(wireless.wireless_latency);
        const hasSignal =
            this.hasValues(wireless.rx_signal) ||
            this.hasValues(wireless.tx_signal) ||
            this.hasValues(wireless.noise_floor);
        const hasLinkScore = this.hasValues(wireless.link_score);

        if (!hasCapacity && !hasLatency && !hasSignal && !hasLinkScore) {
            return;
        }

        const section = document.createElement("div");
        section.className = "mt-3";
        parent.appendChild(section);
        const heading = document.createElement("div");
        heading.className = "d-flex align-items-center justify-content-between mb-1";
        heading.innerHTML = `<h6 class="text-secondary fw-semibold mb-0">Wireless</h6>`;
        section.appendChild(heading);

        const infoLine = document.createElement("div");
        infoLine.className = "small mb-2";
        const distance = this.latestValue(wireless.distance_meters);
        if (distance !== null) {
            infoLine.innerHTML += `<span class="me-3"><i class="fa fa-ruler-horizontal me-1"></i>Distance: ${this.formatNumber(distance, 1)} m</span>`;
        }
        const modulation = this.latestValue(wireless.rx_modulation);
        if (modulation) {
            infoLine.innerHTML += `<span class="text-muted redactable"><i class="fa fa-wave-square me-1"></i>${modulation}</span>`;
        }
        if (infoLine.innerHTML.length > 0) {
            section.appendChild(infoLine);
        }

        if (hasCapacity) {
            const graphId = `${this.id}_capacity_${device.device_hash}`;
            section.appendChild(this.makeGraphDiv(graphId));
            const series = [];
            if (hasUplink) {
                series.push({
                    name: "Uplink Capacity",
                    values: wireless.uplink_capacity,
                    formatter: (v) => this.formatScaled(v)
                });
            }
            if (hasDownlink) {
                series.push({
                    name: "Downlink Capacity",
                    values: wireless.downlink_capacity,
                    formatter: (v) => this.formatScaled(v)
                });
            }
            this.buildChart(
                graphId,
                wireless.ticks || [],
                series,
                (v) => this.formatScaled(v),
                (name, v, formatter) => (formatter ? formatter(v) : this.formatNumber(v, 1)),
                {
                    title: "Estimated Capacity",
                    subtitle: "Estimated by UISP, based on modulation",
                    legendBottom: true
                }
            );
        }

        if (hasLatency) {
            const graphId = `${this.id}_latency_${device.device_hash}`;
            section.appendChild(this.makeGraphDiv(graphId));
            const formatLatency = (v) => `${this.formatNumber(v, 1)} ms`;
            const series = [
                {
                    name: "Wireless Latency (ms)",
                    values: wireless.wireless_latency,
                    formatter: formatLatency
                }
            ];
            this.buildChart(
                graphId,
                wireless.ticks || [],
                series,
                formatLatency,
                (name, v, formatter) => (formatter ? formatter(v) : formatLatency(v)),
                {
                    leftGridSize: 70,
                    title: "Wireless Latency",
                    subtitle: "Estimated by UISP",
                    legendBottom: true
                }
            );
        }

        if (hasSignal) {
            const graphId = `${this.id}_signal_${device.device_hash}`;
            section.appendChild(this.makeGraphDiv(graphId));
            const series = [];
            if (this.hasValues(wireless.rx_signal)) {
                series.push({
                    name: "RX Signal (dBm)",
                    values: wireless.rx_signal,
                    formatter: (v) => `${this.formatNumber(v, 1)} dBm`
                });
            }
            if (this.hasValues(wireless.tx_signal)) {
                series.push({
                    name: "TX Signal (dBm)",
                    values: wireless.tx_signal,
                    formatter: (v) => `${this.formatNumber(v, 1)} dBm`
                });
            }
            if (this.hasValues(wireless.noise_floor)) {
                series.push({
                    name: "Noise Floor",
                    values: wireless.noise_floor,
                    formatter: (v) => `${this.formatNumber(v, 1)} dBm`,
                    lineStyle: { type: "dashed" }
                });
            }
            this.buildChart(
                graphId,
                wireless.ticks || [],
                series,
                (v) => this.formatNumber(v, 1),
                (name, v, formatter) => (formatter ? formatter(v) : this.formatNumber(v, 1)),
                { title: "Radio Connection", legendBottom: true }
            );
        }

        if (hasLinkScore) {
            const graphId = `${this.id}_link_${device.device_hash}`;
            section.appendChild(this.makeGraphDiv(graphId));
            const linkHints = Array.isArray(wireless.link_hint) ? wireless.link_hint : [];
            const linkTooltipFormatter = (name, value, formatter, index) => {
                const formatted = formatter ? formatter(value) : this.formatNumber(value, 1);
                const hint = index !== null && index !== undefined ? linkHints[index] : null;
                if (hint === null || hint === undefined) return formatted;
                return `${formatted} (${hint})`;
            };
            const series = [
                {
                    name: "Link Score",
                    values: wireless.link_score,
                    formatter: (v) => this.formatNumber(v, 1)
                }
            ];
            this.buildChart(
                graphId,
                wireless.ticks || [],
                series,
                (v) => this.formatNumber(v, 1),
                linkTooltipFormatter,
                { title: "UISP Link Evaluation", legendBottom: true }
            );
        }

    }

    makeGraphDiv(id) {
        const graph = document.createElement("div");
        graph.id = id;
        graph.classList.add("dashgraph");
        graph.style.height = "240px";
        return graph;
    }

    buildChart(elementId, ticks, seriesDefs, axisFormatter, tooltipFormatter, options = {}) {
        if (!ticks || ticks.length === 0 || !seriesDefs || seriesDefs.length === 0) return;
        const { leftGridSize = 55, title, subtitle, legendBottom = false } = options;
        const dom = document.getElementById(elementId);
        if (!dom) {
            console.warn("Device chart target missing", elementId);
            return;
        }
        const graph = new DashboardGraph(elementId);
        const option = new GraphOptionsBuilder()
            .withLeftGridSize(leftGridSize)
            .withTimeAxis()
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        if (title || subtitle) {
            option.title = {
                text: title || "",
                subtext: subtitle || "",
                left: "center",
                top: 8,
                textStyle: { color: "#ccc", fontSize: 12, fontWeight: 600, fontFamily: "inherit" },
                subtextStyle: { color: "#999", fontSize: 10, fontFamily: "inherit" }
            };
            option.grid = {
                ...(option.grid || {}),
                top: (option.grid && option.grid.top) || 55
            };
        }
        option.xAxis.data = ticks;
        option.legend.data = seriesDefs.map((s) => s.name);
        if (legendBottom) {
            option.legend = {
                ...(option.legend || {}),
                bottom: 0,
                left: "center"
            };
            option.grid = {
                ...(option.grid || {}),
                bottom: (option.grid && option.grid.bottom) || 35
            };
        }
        option.yAxis = {
            type: "value",
            axisLabel: {
                formatter: (val) => (axisFormatter ? axisFormatter(val) : val)
            }
        };
        option.series = seriesDefs.map((series, idx) => ({
            name: series.name,
            type: "line",
            smooth: true,
            showSymbol: false,
            connectNulls: false,
            data: ticks.map((_, i) => (series.values ? series.values[i] ?? null : null)),
            itemStyle: {
                color: window.graphPalette[idx % window.graphPalette.length]
            },
            lineStyle: series.lineStyle || {}
        }));
        option.tooltip = {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                label: {
                    backgroundColor: "rgba(30,30,30,0.92)",
                    color: "#fff",
                    fontFamily: "inherit",
                    formatter: function (params) {
                        if (params.axisDimension === "x") {
                            return new Date(params.value * 1000).toLocaleString();
                        }
                        return axisFormatter ? axisFormatter(params.value) : params.value;
                    }
                }
            },
            backgroundColor: "rgba(30,30,30,0.92)",
            textStyle: {
                fontSize: 10,
                color: "#fff",
                fontFamily: "inherit"
            },
            formatter: function (params) {
                if (!params || params.length === 0) return "";
                let result = '<div style="font-size: 10px;">';
                result += new Date(params[0].axisValue * 1000).toLocaleString() + "<br/>";
                params.forEach((p) => {
                    const formatter = seriesDefs[p.seriesIndex] ? seriesDefs[p.seriesIndex].formatter : undefined;
                    const formatted = tooltipFormatter
                        ? tooltipFormatter(p.seriesName, p.value, formatter, p.dataIndex)
                        : p.value;
                    result += `${p.marker} ${p.seriesName}: ${formatted}<br/>`;
                });
                result += "</div>";
                return result;
            }
        };

        graph.chart.hideLoading();
        graph.chart.setOption(option);
        this.graphs.push(graph);
    }

    buildBadge(text, variant) {
        const span = document.createElement("span");
        span.className = `badge bg-${variant}`;
        span.innerText = text;
        return span;
    }

    hasValues(series) {
        return Array.isArray(series) && series.some((v) => v !== null && v !== undefined);
    }

    latestValue(series) {
        if (!Array.isArray(series)) return null;
        for (let i = series.length - 1; i >= 0; i--) {
            const v = series[i];
            if (v !== null && v !== undefined) {
                return v;
            }
        }
        return null;
    }

    formatDate(epochSeconds) {
        if (epochSeconds === null || epochSeconds === undefined) return null;
        const d = new Date(Number(epochSeconds) * 1000);
        return d.toLocaleString();
    }

    formatNumber(value, digits = 1) {
        if (value === null || value === undefined || isNaN(value)) return "-";
        return Number(value).toFixed(digits);
    }

    formatScaled(value) {
        if (value === null || value === undefined || isNaN(value)) return "-";
        return scaleNumber(Number(value), 1);
    }

    uptimeWentBackwards(series) {
        if (!Array.isArray(series) || series.length === 0) return false;
        let last = null;
        for (let i = 0; i < series.length; i++) {
            const v = series[i];
            if (v === null || v === undefined) continue;
            if (last !== null && v < last) {
                return true;
            }
            last = v;
        }
        return false;
    }
}

const SUSTAINED_SAMPLE_RATIO = 0.10;

function buildDeviceIssues(devices, circuitCapacity) {
    const issues = [];
    if (!Array.isArray(devices) || devices.length === 0) return issues;

    const downLimitMbps = Number.isFinite(circuitCapacity?.download_max_mbps)
        ? Number(circuitCapacity.download_max_mbps)
        : null;
    const upLimitMbps = Number.isFinite(circuitCapacity?.upload_max_mbps)
        ? Number(circuitCapacity.upload_max_mbps)
        : null;
    const downLimitBps = downLimitMbps !== null ? downLimitMbps * 1000000 : null;
    const upLimitBps = upLimitMbps !== null ? upLimitMbps * 1000000 : null;

    devices.forEach((device) => {
        const deviceName = device?.device_name || device?.device_id || "Device";
        const general = device?.general_timeseries || {};
        const gTicks = Array.isArray(general.ticks) ? general.ticks : [];

        const latestUptime = latestFiniteValue(general.uptime_seconds);
        if (latestUptime !== null && latestUptime < 1800) {
            issues.push({
                time_range: null,
                severity: "warn",
                issue: "Device uptime low",
                details: `Device: ${deviceName} - latest uptime ${formatMinutes(latestUptime)}`,
            });
        }

        const cpuIssue = issueForSustained(
            general.cpu_percent,
            gTicks,
            95,
            "Device CPU high",
            deviceName,
            "%"
        );
        if (cpuIssue) issues.push(cpuIssue);

        const memIssue = issueForSustained(
            general.memory_percent,
            gTicks,
            95,
            "Device memory high",
            deviceName,
            "%"
        );
        if (memIssue) issues.push(memIssue);

        const wireless = device?.wireless_timeseries || {};
        const wTicks = Array.isArray(wireless.ticks) ? wireless.ticks : [];

        if (downLimitBps !== null && Array.isArray(wireless.downlink_capacity)) {
            const medianDown = medianFinite(wireless.downlink_capacity);
            if (medianDown !== null && medianDown < downLimitBps) {
                issues.push({
                    time_range: null,
                    severity: "warn",
                    issue: "Wireless downlink capacity below offered",
                    details: `Device: ${deviceName} - median ${formatMbps(medianDown)} < offered ${formatMbps(downLimitBps)}`,
                });
            }
        }

        if (upLimitBps !== null && Array.isArray(wireless.uplink_capacity)) {
            const medianUp = medianFinite(wireless.uplink_capacity);
            if (medianUp !== null && medianUp < upLimitBps) {
                issues.push({
                    time_range: null,
                    severity: "warn",
                    issue: "Wireless uplink capacity below offered",
                    details: `Device: ${deviceName} - median ${formatMbps(medianUp)} < offered ${formatMbps(upLimitBps)}`,
                });
            }
        }

        const snrRx = buildSnrSeries(wireless.rx_signal, wireless.noise_floor);
        const snrTx = buildSnrSeries(wireless.tx_signal, wireless.noise_floor);
        const snrRxIssue = issueForChange(
            snrRx,
            wTicks,
            10,
            "Radio SNR changed (RX)",
            deviceName,
            " dB"
        );
        if (snrRxIssue) issues.push(snrRxIssue);
        const snrTxIssue = issueForChange(
            snrTx,
            wTicks,
            10,
            "Radio SNR changed (TX)",
            deviceName,
            " dB"
        );
        if (snrTxIssue) issues.push(snrTxIssue);

        const linkIssue = issueForRelativeChange(
            wireless.link_score,
            wTicks,
            0.10,
            "Link score changed",
            deviceName
        );
        if (linkIssue) issues.push(linkIssue);
    });

    return issues;
}

function issueForSustained(series, ticks, threshold, issueLabel, deviceName, unitLabel) {
    const values = Array.isArray(series) ? series : [];
    const times = Array.isArray(ticks) ? ticks : [];
    const len = Math.min(values.length, times.length);
    if (len < 2) return null;

    let total = 0;
    let above = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    let maxValue = null;

    for (let i = 0; i < len; i += 1) {
        const v = values[i];
        if (!Number.isFinite(v)) continue;
        total += 1;
        if (maxValue === null || v > maxValue) maxValue = v;
        if (v >= threshold) {
            above += 1;
            if (firstIdx === -1) firstIdx = i;
            lastIdx = i;
        }
    }

    if (total === 0) return null;
    if (above / total < SUSTAINED_SAMPLE_RATIO) return null;

    const timeRange = (firstIdx >= 0 && lastIdx >= 0)
        ? formatTimeRange(times[firstIdx], times[lastIdx])
        : null;
    const pct = Math.round((above / total) * 100);
    const peakText = maxValue !== null ? `${maxValue.toFixed(1)}${unitLabel}` : null;
    const details = peakText
        ? `Device: ${deviceName} - >=${threshold}${unitLabel} in ${pct}% of samples (peak ${peakText})`
        : `Device: ${deviceName} - >=${threshold}${unitLabel} in ${pct}% of samples`;

    return {
        time_range: timeRange,
        severity: "warn",
        issue: issueLabel,
        details,
    };
}

function issueForChange(series, ticks, threshold, issueLabel, deviceName, unitLabel) {
    const values = Array.isArray(series) ? series : [];
    const times = Array.isArray(ticks) ? ticks : [];
    const len = Math.min(values.length, times.length);
    if (len < 2) return null;

    const first = firstFinite(values, len);
    const last = lastFinite(values, len);
    if (!first || !last || first.idx === last.idx) return null;

    const delta = last.value - first.value;
    if (Math.abs(delta) < threshold) return null;

    const timeRange = formatTimeRange(times[first.idx], times[last.idx]);
    const details = `Device: ${deviceName} - changed by ${formatSigned(delta, 1)}${unitLabel}`;

    return {
        time_range: timeRange,
        severity: "warn",
        issue: issueLabel,
        details,
    };
}

function issueForRelativeChange(series, ticks, thresholdRatio, issueLabel, deviceName) {
    const values = Array.isArray(series) ? series : [];
    const times = Array.isArray(ticks) ? ticks : [];
    const len = Math.min(values.length, times.length);
    if (len < 2) return null;

    const first = firstFinite(values, len);
    const last = lastFinite(values, len);
    if (!first || !last || first.idx === last.idx) return null;
    if (!Number.isFinite(first.value) || Math.abs(first.value) < 1e-6) return null;

    const delta = last.value - first.value;
    const ratio = Math.abs(delta / first.value);
    if (ratio < thresholdRatio) return null;

    const timeRange = formatTimeRange(times[first.idx], times[last.idx]);
    const details = `Device: ${deviceName} - changed by ${formatSigned(delta / first.value * 100, 0)}% (${formatScore(first.value)} → ${formatScore(last.value)})`;

    return {
        time_range: timeRange,
        severity: "warn",
        issue: issueLabel,
        details,
    };
}

function buildSnrSeries(signal, noise) {
    if (!Array.isArray(signal) || !Array.isArray(noise)) return [];
    const len = Math.min(signal.length, noise.length);
    const snr = new Array(len);
    for (let i = 0; i < len; i += 1) {
        const s = signal[i];
        const n = noise[i];
        snr[i] = (Number.isFinite(s) && Number.isFinite(n)) ? (s - n) : null;
    }
    return snr;
}

function medianFinite(series) {
    if (!Array.isArray(series)) return null;
    const values = series.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
    if (values.length === 0) return null;
    const mid = Math.floor(values.length / 2);
    if (values.length % 2 === 0) {
        return (values[mid - 1] + values[mid]) / 2;
    }
    return values[mid];
}

function firstFinite(values, len) {
    const max = Number.isFinite(len) ? len : values.length;
    for (let i = 0; i < max; i += 1) {
        const v = values[i];
        if (Number.isFinite(v)) {
            return { idx: i, value: v };
        }
    }
    return null;
}

function lastFinite(values, len) {
    const max = Number.isFinite(len) ? len : values.length;
    for (let i = max - 1; i >= 0; i -= 1) {
        const v = values[i];
        if (Number.isFinite(v)) {
            return { idx: i, value: v };
        }
    }
    return null;
}

function latestFiniteValue(series) {
    if (!Array.isArray(series)) return null;
    for (let i = series.length - 1; i >= 0; i -= 1) {
        const v = series[i];
        if (Number.isFinite(v)) return v;
    }
    return null;
}

function formatSigned(value, digits = 1) {
    const sign = value >= 0 ? "+" : "";
    return `${sign}${Number(value).toFixed(digits)}`;
}

function formatMinutes(seconds) {
    const minutes = Number(seconds) / 60;
    if (!Number.isFinite(minutes)) return "-";
    return `${minutes.toFixed(0)} minutes`;
}

function formatMbps(bitsPerSecond) {
    if (!Number.isFinite(bitsPerSecond)) return "-";
    const mbps = bitsPerSecond / 1000000;
    return `${mbps.toFixed(1)} Mbps`;
}

function formatScore(value) {
    if (!Number.isFinite(value)) return "-";
    const digits = Math.abs(value) >= 10 ? 1 : 2;
    return Number(value).toFixed(digits);
}
